import * as React from 'react';
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { Edit, Eye, MoreHorizontal, UserX, Users } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import type { Employee } from '@repo/types';
import { RoleBadge } from './RoleBadge';
import { StatusBadge } from './StatusBadge';

interface EmployeeTableProps {
  data: Employee[];
  onRowClick?: (employee: Employee) => void;
  onEditClick?: (employee: Employee) => void;
  onDeactivateClick?: (employee: Employee) => void;
  className?: string;
}

const AVATAR_TINTS = [
  'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  'bg-rose-500/10 text-rose-600 dark:text-rose-400',
  'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
];

function tintFor(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_TINTS[Math.abs(hash) % AVATAR_TINTS.length];
}

function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

export function EmployeeTable({
  data,
  onRowClick,
  onEditClick,
  onDeactivateClick,
  className,
}: EmployeeTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const columns: ColumnDef<Employee>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} label="Employee" />,
      cell: ({ row }) => {
        const employee = row.original;
        return (
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                tintFor(employee.name || employee.email)
              )}
            >
              {initials(employee.name)}
            </span>
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => onRowClick?.(employee)}
                className="block max-w-full truncate text-left font-medium underline-offset-2 transition-colors hover:text-primary hover:underline"
              >
                {employee.name}
              </button>
              <div className="truncate text-xs text-muted-foreground">{employee.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => <RoleBadge role={row.original.role} />,
    },
    {
      id: 'team',
      header: 'Team',
      cell: ({ row }) =>
        row.original.team ? (
          <span className="text-sm">{row.original.team.name}</span>
        ) : (
          <span className="text-sm text-muted-foreground">Unassigned</span>
        ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => {
        const employee = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onRowClick?.(employee)}>
                <Eye className="size-4" />
                View profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEditClick?.(employee)}>
                <Edit className="size-4" />
                Edit employee
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => {
                  if (employee.status === 'active') onDeactivateClick?.(employee);
                }}
                disabled={employee.status !== 'active'}
              >
                <UserX className="size-4" />
                {employee.status === 'active' ? 'Deactivate' : 'Inactive'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <DataTable
      table={table}
      className={className}
      emptyState={
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Users className="size-6 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>No employees found</EmptyTitle>
            <EmptyDescription>Try adjusting your filters or add a new employee.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      }
    />
  );
}
